import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { LocalStorageService } from '../../local-storage/local-storage.service';
import { SoporteDocumentalService } from '../../services/soporte-documental/soporte-documental.service';

interface DocumentoSoporte {
  id: number | null;
  nombre: string;
  idDocSoporte: number | null;
  obligatorio: boolean;
  tipoDocumento: string;
  archivo?: File | null;
  rutaArchivo?: string | null;
  cargado?: boolean;
}

@Component({
  selector: 'app-soporte-documentos-card',
  templateUrl: './soporte-documentos-card.component.html',
  styleUrls: ['./soporte-documentos-card.component.css'],
})
export class SoporteDocumentosCardComponent implements OnInit {
  @Input() nombrePersona: string = '';
  @Input() identificacionUsuario: string = '';
  @Input() storageKey!: string;
  @Input() documentosGenerales: any[] = [];
  @Input() documentosEspecificos: any[] = [];
  @Output() estadoCompleto = new EventEmitter<boolean>();

  documentos: DocumentoSoporte[] = [];

  get generales(): DocumentoSoporte[] {
    return this.documentos.filter((d) => d.tipoDocumento === 'Generales');
  }

  get especificos(): DocumentoSoporte[] {
    return this.documentos.filter((d) => d.tipoDocumento === 'Específicos');
  }

  get documentosState(): DocumentoSoporte[] {
    return this.documentos;
  }

  constructor(
    private localStorageService: LocalStorageService,
    private soporteDocumentalService: SoporteDocumentalService,
  ) {}

  ngOnInit(): void {
    this.documentos = [
      ...this.documentosGenerales.map((d) => ({
        id: d.idDocSoporte ?? null,
        idDocSoporte: d.id,
        nombre: d.nombreDocumento,
        obligatorio: d.obligatorio,
        tipoDocumento: d.tipoDocumento,
        rutaArchivo: d.rutaArchivo,
        cargado: d.cargado,
      })),
      ...this.documentosEspecificos.map((d) => ({
        id: d.idDocSoporte ?? null,
        idDocSoporte: d.id,
        nombre: d.nombreDocumento,
        obligatorio: d.obligatorio,
        tipoDocumento: d.tipoDocumento,
        rutaArchivo: d.rutaArchivo,
        cargado: d.cargado,
      })),
    ];

    this.restoreFromStorage();
    this.emitirEstado();
  }

  onFileSelected(
    event: Event,
    doc: DocumentoSoporte,
    input: HTMLInputElement,
  ): void {
    const file = input.files?.[0];
    if (!file) return;

    doc.archivo = file;

    if (!doc.idDocSoporte) {
      console.error('idDocSoporte es requerido');
      return;
    }

    this.soporteDocumentalService
      // .subirArchivo(file, doc.id, this.identificacionUsuario)
      .subirArchivo(file, doc.idDocSoporte!, this.identificacionUsuario)
      .subscribe({
        next: (response) => {
          doc.id = response.id;
          doc.cargado = true;
          doc.rutaArchivo = response.rutaArchivo;

          this.saveToStorage();
          this.emitirEstado();
        },
        error: (err) => {
          console.error('Error subiendo archivo', err);

          doc.archivo = null;
          doc.cargado = false;
          input.value = '';
        },
      });
  }

  deleteFile(doc: DocumentoSoporte, input?: HTMLInputElement): void {
    if (doc.id) {
      this.soporteDocumentalService.eliminarArchivo(doc.id).subscribe({
        next: () => {
          this.limpiarDocumento(doc, input);
        },
        error: (err) => {
          console.error('Error eliminando archivo', err);
        },
      });
    } else {
      this.limpiarDocumento(doc, input);
    }
  }

  private limpiarDocumento(doc: DocumentoSoporte, input?: HTMLInputElement) {
    doc.archivo = null;
    doc.rutaArchivo = null;
    doc.cargado = false;
    doc.id = null;

    if (input) input.value = '';

    this.saveToStorage();
    this.emitirEstado();
  }

  visualizeFile(doc: DocumentoSoporte): void {
    if (doc.archivo) {
      const url = URL.createObjectURL(doc.archivo);
      window.open(url, '_blank');
      return;
    }

    if (doc.id) {
      this.soporteDocumentalService.descargarArchivo(doc.id).subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
        },
        error: (err) => {
          console.error('Error descargando archivo', err);
        },
      });
    }
  }

  isCompleted(): boolean {
    return this.documentos
      .filter((d) => d.obligatorio)
      .every((d) => !!d.archivo || d.cargado);
  }

  private emitirEstado(): void {
    this.estadoCompleto.emit(this.isCompleted());
  }

  private restoreFromStorage(): void {
    const saved: any[] = this.localStorageService.load(this.storageKey);
    if (!saved) return;

    this.documentos.forEach((doc) => {
      const found = saved.find((s) => s.nombre === doc.nombre);
      if (found) {
        const blob = this.base64ToBlob(found.archivoBase64, found.archivoTipo);
        doc.archivo = new File([blob], found.archivoNombre, {
          type: found.archivoTipo,
        });
      }
    });
  }

  private base64ToBlob(base64: string, type: string): Blob {
    const byteString = atob(base64.split(',')[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uintArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      uintArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([arrayBuffer], { type });
  }

  getFileName(ruta: string | null | undefined): string {
    if (!ruta) return '';
    return ruta.split('/').pop() || '';
  }

  private saveToStorage(): void {
    const data: any[] = [];
    const readers = this.documentos
      .filter((d) => d.archivo)
      .map(
        (doc) =>
          new Promise<void>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              data.push({
                nombre: doc.nombre,
                archivoNombre: doc.archivo!.name,
                archivoTipo: doc.archivo!.type,
                archivoBase64: reader.result as string,
              });
              resolve();
            };
            reader.readAsDataURL(doc.archivo!);
          }),
      );

    Promise.all(readers).then(() => {
      this.localStorageService.save(this.storageKey, data);
    });
  }
}
